import * as _ from 'underscore';

export interface ICronExpressionFields<T> {
  seconds: T;
  minutes: T;
  hours: T;
  dayOfMonth: T;
  month: T;
  dayOfWeek: T;
  year?: T;
}

// Used to represent seconds, minutes, or hours
// Specific indicates a specific time otherwise increment/from/to describe a range & interval
// Seconds represented as seconds, 0-59
// Minutes represented as minutes, 0-59
// Hours represented as hours, 0-23
export interface ICronExpressionFieldTimeSubsetParts {
  specific?: number;
  increment?: number;
  from?: number;
  to?: number;
}

export class CronExpressionDefaultValues {
  public static seconds = '0';
  public static minutes = '0';
  public static hours = '0';
  public static dayOfMonth = '?';
  public static month = '*';
  public static dayOfWeek = '*';
  public static year = '';

  public static expression =
    CronExpressionDefaultValues.seconds + ' ' +
    CronExpressionDefaultValues.minutes + ' ' +
    CronExpressionDefaultValues.hours + ' ' +
    CronExpressionDefaultValues.dayOfMonth + ' ' +
    CronExpressionDefaultValues.month + ' ' +
    CronExpressionDefaultValues.dayOfWeek + ' ' +
    CronExpressionDefaultValues.year;
}

export class CronExpressionFieldValues implements ICronExpressionFields<string> {
  constructor(
    public seconds = CronExpressionDefaultValues.seconds,
    public minutes = CronExpressionDefaultValues.minutes,
    public hours = CronExpressionDefaultValues.hours,
    public dayOfMonth = CronExpressionDefaultValues.dayOfMonth,
    public month = CronExpressionDefaultValues.month,
    public dayOfWeek = CronExpressionDefaultValues.dayOfWeek,
    public year = CronExpressionDefaultValues.year) { }
}

export class CronExpressionFieldSubsetTypes {
  public static All = '1'; // ie '*'
  public static Any = '2'; // ie '?'
  public static Specific = '3'; // ie '3'
  public static SpecificIncrement = '4'; // ie '0/2'
  public static Range = '5'; // ie '1-5'
  public static RangeIncrement = '6'; // ie '1-5/2'
  public static NthWeekday = '7'; // ie '5#1', the 1st Thursday of the month
  public static LastWeekday = '8'; // ie '4L', last Wednesday of the month
  public static Invalid = '9';
}

// Cron expression values for day of week are 1-7, Sunday through Saturday
export interface IDayOfWeekViewModel {
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
}

export interface IHoursOfDayViewModel {
  specific: number; // represented as minutes past midnight
  increment: number; // represented as minutes
  from: number; // represented as minutes past midnight
  to: number; // represented as minutes past midnight
}

export enum RemoteRefreshScheduleMode {
  None,
  Hourly,
  Daily
}

export class RemoteRefreshScheduleEditorViewModel {

  public static minuteIncrementOptions = [15, 30, 1 * 60, 2 * 60, 4 * 60, 8 * 60, 12 * 60]; // represented as minutes

  private static defaultHoursOfDay: IHoursOfDayViewModel = {
    specific: 5 * 60, // 5 AM
    increment: 2 * 60, // 2 hours
    from: 8 * 60, // 8 AM
    to: 17 * 60, // 5 PM
  };
  private static defaultDayOfWeek: IDayOfWeekViewModel = {
    sunday: false,
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false
  };
  private static emptyDayOfWeek: IDayOfWeekViewModel = {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false
  };

  public hoursOfDay: IHoursOfDayViewModel;
  public dayOfWeek: IDayOfWeekViewModel;
  public editorMode: RemoteRefreshScheduleMode;
  public editorModeName = () => RemoteRefreshScheduleMode[this.editorMode];

  constructor(defaultScheduleMode: RemoteRefreshScheduleMode = RemoteRefreshScheduleMode.Daily) {
    this.hoursOfDay = _.clone(RemoteRefreshScheduleEditorViewModel.defaultHoursOfDay);
    this.dayOfWeek = _.clone(RemoteRefreshScheduleEditorViewModel.defaultDayOfWeek);

    this.editorMode = defaultScheduleMode;
  }

  // Get the cron expression equivalent to the current view model
  public getCronExpression(): string {
    const values = new CronExpressionFieldValues();

    values.minutes = this.buildMinutesExpression();
    values.hours = this.buildHoursExpression();
    values.dayOfWeek = this.buildDayOfWeekExpression();

    return CronExpressionHelper.buildFromFields(values);
  }

  // This does NOT validate the expression string, assumes that it is a valid cron expression.
  // This only supports the subset of cron expressions currently implemented by the UI.
  public setFromCronExpression(expression: string) {
    const minutesField = CronExpressionHelper.getMinutesField(expression);
    const hoursField = CronExpressionHelper.getHoursField(expression);
    const dayOfWeekField = CronExpressionHelper.getDayOfWeekField(expression);
    let days = [ false, false, false, false, false, false, false ];

    // minutes and hours of day
    const minutesParts = CronExpressionHelper.getTimeFieldSubsetParts(minutesField);
    const hoursParts = CronExpressionHelper.getTimeFieldSubsetParts(hoursField);

    // Expression can be an incremented range or a specific. Increment is described
    // using hours or minutes.
    if (minutesParts.increment !== undefined || hoursParts.increment !== undefined) {
      this.editorMode = RemoteRefreshScheduleMode.Hourly;
      this.hoursOfDay.increment = (minutesParts.increment !== undefined ? minutesParts.increment : 0) +
        (hoursParts.increment !== undefined ? hoursParts.increment * 60 : 0);
      this.hoursOfDay.from = hoursParts.from * 60;
      this.hoursOfDay.to = hoursParts.to * 60;
    } else {
      this.editorMode = RemoteRefreshScheduleMode.Daily;
      this.hoursOfDay.specific = hoursParts.specific * 60;
    }

    // days of week

    _.each(dayOfWeekField.split(','), (subset: string) => {
      const type = CronExpressionHelper.getTypeFromFieldSubset(subset);

      if (type === CronExpressionFieldSubsetTypes.Range) {
        const from = CronExpressionHelper.getFromPartFromSubset(subset);
        const to = CronExpressionHelper.getToPartFromSubset(subset);
        _.each(_.range(from, to + 1), (dayNumber: number) => days[dayNumber - 1] = true);
      } else if (type === CronExpressionFieldSubsetTypes.Specific) {
        days[CronExpressionHelper.getSpecificPartFromSubset(subset) - 1] = true;
      } else if (type === CronExpressionFieldSubsetTypes.All) {
        days = [ true, true, true, true, true, true, true];
      } else {
        throw '#setFromCronExpression failed when parsing the dayOfWeek field of the expression: {' + expression + '}. #getTypeFromFieldSubset returned: {' + type + '} for subset parameter: {' + subset + '}';
      }
    });

    this.dayOfWeek = {
      sunday: days[0],
      monday: days[1],
      tuesday: days[2],
      wednesday: days[3],
      thursday: days[4],
      friday: days[5],
      saturday: days[6]
    };
  }

  public isValid(): boolean {
    return (this.editorMode !== RemoteRefreshScheduleMode.None) && this.intervalIsValid() && this.dayOfWeekNotEmpty();
  }

  public static isEqual(objThis: RemoteRefreshScheduleEditorViewModel, objThat: RemoteRefreshScheduleEditorViewModel): boolean {
    return _.isEqual(objThis.hoursOfDay, objThat.hoursOfDay) &&
      _.isEqual(objThis.dayOfWeek, objThat.dayOfWeek) &&
      objThis.editorMode === objThat.editorMode;
  }

  public isEqual(that: RemoteRefreshScheduleEditorViewModel): boolean {
    return RemoteRefreshScheduleEditorViewModel.isEqual(this, that);
  }

  public static clone(objThis: RemoteRefreshScheduleEditorViewModel, objThat: RemoteRefreshScheduleEditorViewModel): RemoteRefreshScheduleEditorViewModel {
    objThis.hoursOfDay = _.clone(objThat.hoursOfDay);
    objThis.dayOfWeek = _.clone(objThat.dayOfWeek);
    objThis.editorMode = objThat.editorMode;

    return objThis;
  }

  public clone(that: RemoteRefreshScheduleEditorViewModel): RemoteRefreshScheduleEditorViewModel {
    return RemoteRefreshScheduleEditorViewModel.clone(this, that);
  }

  private intervalIsValid(): boolean {
    return (this.editorMode === RemoteRefreshScheduleMode.Daily) || this.hoursOfDay.from < this.hoursOfDay.to;
  }

  private dayOfWeekNotEmpty(): boolean {
    return !_.isEqual(this.dayOfWeek, RemoteRefreshScheduleEditorViewModel.emptyDayOfWeek);
  }

  // Build the minutes cron expression field from the view model
  // Cron expression values for minutes are 0-59
  private buildMinutesExpression(): string {
    if (this.editorMode === RemoteRefreshScheduleMode.Daily) {
      return '0';
    } else {
      const minutesIncrement = this.hoursOfDay.increment % 60;
      return '0' + (minutesIncrement > 0 ? '/' + minutesIncrement : '');
    }
  }

  // Build the hours cron expression field from the view model
  // Cron expression values for hours are 0-23
  private buildHoursExpression(): string {
    if (this.editorMode === RemoteRefreshScheduleMode.Daily) {
      return (this.hoursOfDay.specific / 60) + '';
    } else {
      // The view model combines cron's minutes & hours into a single increment field described in minutes.

      const minutesIncrement = this.hoursOfDay.increment % 60;
      const hoursIncrement = (this.hoursOfDay.increment >= 60 ? this.hoursOfDay.increment - minutesIncrement : 0) / 60;

      if (hoursIncrement > 0) {
        return (this.hoursOfDay.from / 60) + '-' + (this.hoursOfDay.to / 60) + '/' + hoursIncrement;
      } else {
        return (this.hoursOfDay.from / 60) + '-' + (this.hoursOfDay.to / 60);
      }
    }
  }

  // Build the day of week cron expression field from the view model
  // Collapse the days of week into the minimum cron expression so that it can be localized and translated into
  // the most succinct statement possible. Cron expression values for day of week are 1-7, Sunday through Saturday.
  private buildDayOfWeekExpression(): string {
    const days = [ this.dayOfWeek.sunday,
      this.dayOfWeek.monday,
      this.dayOfWeek.tuesday,
      this.dayOfWeek.wednesday,
      this.dayOfWeek.thursday,
      this.dayOfWeek.friday,
      this.dayOfWeek.saturday ];

    let expression = '';
    let indexOfStart = days.indexOf(true);
    let indexOfEnd = days.indexOf(false, indexOfStart);

    if (indexOfStart === -1) {
      return '?';
    }

    if (indexOfStart === 0 && indexOfEnd === -1) {
      return '*';
    }

    while (true) {
      indexOfEnd = days.indexOf(false, indexOfStart);

      if (indexOfEnd === -1) { // end edge not found
        if (indexOfStart === days.length - 1) {
          expression = expression + (indexOfStart + 1) + '';
        } else if (indexOfStart === days.length - 2) {
          expression = expression + (indexOfStart + 1) + ',' + (indexOfStart + 2);
        } else {
          expression = expression + (indexOfStart + 1) + '-' + (days.length);
        }
        break;
      } else { // end edge found
        if (indexOfEnd - indexOfStart === 1) {
          expression = expression + (indexOfStart + 1);
        }else if (indexOfEnd - indexOfStart === 2) {
          expression = expression + (indexOfStart + 1) + ',' + indexOfEnd;
        } else {
          expression = expression + (indexOfStart + 1) + '-' + indexOfEnd;
        }

        indexOfStart = days.indexOf(true, indexOfEnd);
        if (indexOfStart !== -1) {
          expression = expression + ',';
        } else {
          break;
        }
      }
    }

    return expression;
  }

}

export module CronExpressionHelper {

  // Helper methods for cron expressions in the Quartz Scheduler format.
  // Docs: http://www.quartz-scheduler.org/documentation/quartz-1.x/tutorials/crontrigger
  //
  // Expression consists of 7 fields:
  //   _Field_      _Mandatory_   _Values_          _Special_     _Notes_
  //   Seconds      true          0-59              , - * /
  //   Minutes      true          0-59              , - * /
  //   Hours        true          0-23              , - * /
  //   Day of month true          1-31              , - * ? / L W
  //   Month        true          1-12              , - * /       Jan-Dec
  //   Day of week  true          1-7               , - * ? / L # Sun-Sat
  //   Year         false         empty, 1970-2099  , - * /
  //
  // WARN: Non-numeric values are not supported by this implementation (ie 'Jan' or 'Sun')

  // Concatenate discrete fields into a single cron expression string
  export function buildFromFields(fields: ICronExpressionFields<string>): string {
    let expression =
      fields.seconds + ' ' +
      fields.minutes + ' ' +
      fields.hours + ' ' +
      fields.dayOfMonth + ' ' +
      fields.month + ' ' +
      fields.dayOfWeek;

    // year is optional
    if (fields.year) {
      expression = expression + ' ' + fields.year;
    }

    return expression;
  }

  // Parse discrete fields from a cron expression string.
  export function buildFromExpression(expression: string): ICronExpressionFields<string> {
    const fields = new CronExpressionFieldValues();
    const split = expression.split(' ');

    fields.seconds = split[0];
    fields.minutes = split[1];
    fields.hours = split[2];
    fields.dayOfMonth = split[3];
    fields.month = split[4];
    fields.dayOfWeek = split[5];
    if(split.length === 7) {
      fields.year = split[6];
    }

    return fields;
  }

  // Given a subset of a field that is comma delimited, determine the type. This type informs the parsing applied
  // to the field.
  export function getTypeFromFieldSubset(field: string): CronExpressionFieldSubsetTypes {
    if (field === '*') return CronExpressionFieldSubsetTypes.All;

    if (field === '?') return CronExpressionFieldSubsetTypes.Any;

    // A sinle one or two digit number
    if (field.search('^([0-9]|[0-9][0-9])$') !== -1) return CronExpressionFieldSubsetTypes.Specific;

    // A single one or two digit number with a one or two digit increment
    if (field.search('((^)|(^([0-9]|[0-9][0-9])))\/([0-9]|[0-9][0-9])$') !== -1) return CronExpressionFieldSubsetTypes.SpecificIncrement;

    // Two one or two digit numbers separated by a dash, describing the from and to properties of the range.
    if (field.search('^(([0-9]|[0-9][0-9])-([0-9]|[0-9][0-9]))$') !== -1) return CronExpressionFieldSubsetTypes.Range;

    // Two one or two digit numbers separated by a dash, describing the from and to properties of the range, with a one or two digit increment.
    if (field.search('^(([0-9]|[0-9][0-9])-([0-9]|[0-9][0-9]))\/([0-9]|[0-9][0-9])$') !== -1) return CronExpressionFieldSubsetTypes.RangeIncrement;

    // Two one digit numbers separated by a pound, describing the nth weekday of the month.
    if (field.search('^[1-7]#[1-5]$') !== -1) return CronExpressionFieldSubsetTypes.NthWeekday;

    // One digit number followed by 'L', describing the last weekday of the month.
    if (field.search('^[1-5]L$') !== -1) return CronExpressionFieldSubsetTypes.LastWeekday;

    return CronExpressionFieldSubsetTypes.Invalid;
  }

  // Return the "specific" part in a field subset.
  // Example: 1/4 returns 1
  // Example: 4 returns 4
  // Example: 0/2 returns 2
  export function getSpecificPartFromSubset(subset: string): number {
    // input may or may not have an increment
    const slash = subset.indexOf('/');

    // an increment with no specific value is an implied 0
    if (slash === 0) return 0;

    const number = slash === -1 ? subset : subset.substring(0, slash);
    return parseInt(number, 10);
  }

  // Return the "from" part of a range in a field subset.
  // Example: 1-2 returns 1
  // Example: 4-10/2 returns 4
  export function getFromPartFromSubset(subset: string): number {
    const number = subset.substring(0, subset.indexOf('-'));
    return parseInt(number, 10);
  }

  // Return the "to" part of a range in a field subset.
  // Example: 1-2 returns 2
  // Example: 4-10/2 returns 10
  export function getToPartFromSubset(subset: string): number {
    // input may or may not have an increment
    const slash = subset.indexOf('/');
    const number = subset.substring(subset.indexOf('-') + 1, slash === -1 ? subset.length : slash);
    return parseInt(number, 10);
  }

  // Return the "Weekday" part in a field subset.
  // Example: 5#1 returns 5, i.e. Thursday
  // Example: 1L returns 1, i.e. Sunday
  export function getWeekdayPartFromSubset(subset: string): number {
    let index = subset.indexOf('#');
    if (index === -1) {
      index = subset.indexOf('L');
    }
    const number = subset.substring(0, index);
    return parseInt(number, 10);
  }

  // Return the "Nth" part in a field subset.
  // Example: 5#1 returns 1
  export function getNthPartFromSubset(subset: string): number {
    const number = subset.substring(subset.indexOf('#') + 1, subset.length);
    return parseInt(number, 10);
  }

  // Return the "increment" part in a field subset.
  // Example: 2/5 returns 5
  // Example: 4-10/2 returns 2
  export function getIncrementPartFromSubset(subset: string) {
    const number = subset.substring(subset.indexOf('/') + 1);
    return parseInt(number, 10);
  }

  // Given a field subset, return a view model equivalent. Subsets are the comma delimited portions of a cron expression field.
  // Example: '8-17/2' returns { increment: 2, from: 8, to: 17 }
  export function getTimeFieldSubsetParts(subset: string): ICronExpressionFieldTimeSubsetParts {
    const parts: ICronExpressionFieldTimeSubsetParts = {};
    const type = CronExpressionHelper.getTypeFromFieldSubset(subset);

    if (type === CronExpressionFieldSubsetTypes.Specific) {
      parts.specific = getSpecificPartFromSubset(subset);
    } else if (type === CronExpressionFieldSubsetTypes.SpecificIncrement) {
      parts.specific = getSpecificPartFromSubset(subset);
      parts.increment = getIncrementPartFromSubset(subset);
    } else if (type === CronExpressionFieldSubsetTypes.Range) {
      parts.from = getFromPartFromSubset(subset);
      parts.to = getToPartFromSubset(subset);
    } else if (type === CronExpressionFieldSubsetTypes.RangeIncrement) {
      parts.from = getFromPartFromSubset(subset);
      parts.to = getToPartFromSubset(subset);
      parts.increment = getIncrementPartFromSubset(subset);
    } else {
      throw '#getTimeFieldSubsetParts failed. #getTypeFromFieldSubset returned: {' + type + '} for subset parameter: {' + subset + '}';
    }

    return parts;
  }

  // Get the seconds field of a cron expression string
  export function getSecondsField(expression: string): string {
    return buildFromExpression(expression).seconds;
  }

  // Get the minutes field of a cron expression string
  export function getMinutesField(expression: string): string {
    return buildFromExpression(expression).minutes;
  }

  // Get the hours field of a cron expression string
  export function getHoursField(expression: string): string {
    return buildFromExpression(expression).hours;
  }

  // Get the day of month field of a cron expression string
  export function getDayOfMonthField(expression: string): string {
    return buildFromExpression(expression).dayOfMonth;
  }

  // Get the month field of a cron expression string
  export function getMonthField(expression: string): string {
    return buildFromExpression(expression).month;
  }

  // Get the day of week field of a cron expression string
  export function getDayOfWeekField(expression: string): string {
    return buildFromExpression(expression).dayOfWeek;
  }

  // Get the year field of a cron expression string
  export function getYearField(expression: string): string {
    return buildFromExpression(expression).year;
  }

  // Set the seconds field of a cron expression string
  export function setSecondsField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.seconds = val;
    return buildFromFields(fields);
  }

  // Set the minutes field of a cron expression string
  export function setMinutesField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.minutes = val;
    return buildFromFields(fields);
  }

  // Set the hours field of a cron expression string
  export function setHoursField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.hours = val;
    return buildFromFields(fields);
  }

  // Set the day of month field of a cron expression string
  export function setDayOfMonthField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.dayOfMonth = val;
    return buildFromFields(fields);
  }

  // Set the month field of a cron expression string
  export function setMonthField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.month = val;
    return buildFromFields(fields);
  }

  // Set the day of week field of a cron expression string
  export function setDayOfWeekField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.dayOfWeek = val;
    return buildFromFields(fields);
  }

  // Set the year field of a cron expression string
  export function setYearField(expression: string, val: string): string {
    const fields = buildFromExpression(expression);
    fields.year = val;
    return buildFromFields(fields);
  }

}
